import React, { useRef } from "react";
import { TextField, Grid, Button, Typography, Checkbox, FormControlLabel, Box } from "@mui/material";
import { Formik, Form } from "formik";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import * as Yup from "yup";

import "./access-health-records.css";
import Signature from "../../../../common/signature/signature";
import GeneratePdf from "../../../../common/generatePdf/generate-pdf";

// Validation schema
const validationSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  surname: Yup.string().required("Surname is required"),
  dob: Yup.string().required("Date of birth is required"),
  nhsNumber: Yup.string().required("NHS number is required"),
  currentAddress: Yup.string().required("Current address is required"),
  previousAddress: Yup.string().required("Previous address is required"),
  previousDoctorName: Yup.string().required("Previous doctor name is required"),
  previousDoctorAddress: Yup.string().required("Previous doctor address is required"),
  guardianFirstName: Yup.string().required("Guardian first name is required"),
  guardianSurname: Yup.string().required("Guardian surname is required"),
  homePhone: Yup.string().required("Home phone number is required"),
  mobilePhone: Yup.string().required("Mobile phone number is required"),
  guardianAddress: Yup.string().required("Guardian address is required"),
  confirm: Yup.boolean().oneOf([true], "Confirmation is required"),
});

const AccessHealthCareRecordsForm = () => {
  const formRef = useRef(null); // Reference to the form DOM
  const signatureRef = useRef(null);
  const signatureSecondRef = useRef(null);

  // Initialize the useGeneratePdf hook with optional filename and options
  const generatePdf = GeneratePdf({ filename: "access-healthcare-records.pdf" });

  return (
    <Formik
      initialValues={{
        firstName: "",
        surname: "",
        dob: "",
        nhsNumber: "",
        currentAddress: "",
        previousAddress: "",
        previousDoctorName: "",
        previousDoctorAddress: "",
        guardianFirstName: "",
        guardianSurname: "",
        homePhone: "",
        mobilePhone: "",
        guardianAddress: "",
        confirm: false,
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const signatureData = signatureRef.current.getSignatureData();
        const signatureSecondData = signatureSecondRef.current.getSignatureData();
        if (!signatureData || !signatureSecondData) {
          alert("Please provide a signature");
          return;
        }

        // Call the reusable generatePdf function and pass the form reference
        generatePdf(formRef);
        const formData = { ...values, signature: signatureData, signatureSecond: signatureSecondData }; // Include signature in form values

        console.log(formData);
      }}
      validateOnMount={false}
      validateOnBlur={true}
    >
      {({ values, handleChange, handleBlur, handleSubmit, setFieldValue, errors, touched }) => (
        <Form onSubmit={handleSubmit} ref={formRef} className='healthcare-form'>
          <Typography sx={{ fontWeight: "bold", fontSize: "22px", color: "black" }} align='left'>
            Medical Consent
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "grey" }}>
            Provide accurate details for the child or young person to ensure proper care and medical <br /> consent.
            Complete all fields and sign where needed.
          </Typography>

          <Typography sx={{ fontSize: "18px", color: "black" }} mt={3}>
            Child / Young Persons Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                First Name
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your First Name'
                name='firstName'
                value={values.firstName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.firstName && Boolean(errors.firstName)}
                helperText={touched.firstName && errors.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Sur Name
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your Sur Name'
                name='surname'
                value={values.surname}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.surname && Boolean(errors.surname)}
                helperText={touched.surname && errors.surname}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Date of Birth
              </Typography>
              <TextField
                fullWidth
                name='dob'
                type='date'
                InputLabelProps={{ shrink: true }}
                value={values.dob}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.dob && Boolean(errors.dob)}
                helperText={touched.dob && errors.dob}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                NHS Number
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your NHS Number'
                name='nhsNumber'
                value={values.nhsNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.nhsNumber && Boolean(errors.nhsNumber)}
                helperText={touched.nhsNumber && errors.nhsNumber}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Current Address
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your Current Address'
                name='currentAddress'
                value={values.currentAddress}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.currentAddress && Boolean(errors.currentAddress)}
                helperText={touched.currentAddress && errors.currentAddress}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Previous Address
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your Previous Address'
                name='previousAddress'
                value={values.previousAddress}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.previousAddress && Boolean(errors.previousAddress)}
                helperText={touched.previousAddress && errors.previousAddress}
              />
            </Grid>
          </Grid>

          <Typography sx={{ fontSize: "18px", color: "black" }} mt={2}>
            Previous Doctor’s Details
          </Typography>
          <Typography sx={{ fontSize: "14px" }}>
            Provide the contact details of the previous doctor for medical history reference and continuity of <br />
            care.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} mt={2}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Previous Doctor's Name
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter your Previous Doctor's Name"
                name='previousDoctorName'
                value={values.previousDoctorName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.previousDoctorName && Boolean(errors.previousDoctorName)}
                helperText={touched.previousDoctorName && errors.previousDoctorName}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Previous Doctor's Address
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter your Previous Doctor's Address"
                name='previousDoctorAddress'
                value={values.previousDoctorAddress}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.previousDoctorAddress && Boolean(errors.previousDoctorAddress)}
                helperText={touched.previousDoctorAddress && errors.previousDoctorAddress}
              />
            </Grid>
          </Grid>

          <Typography sx={{ fontSize: "18px", color: "black" }} mt={2}>
            Parent/Guardian Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                First Name
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your First Name'
                name='guardianFirstName'
                value={values.guardianFirstName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.guardianFirstName && Boolean(errors.guardianFirstName)}
                helperText={touched.guardianFirstName && errors.guardianFirstName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Sur Name
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your Sur Name'
                name='guardianSurname'
                value={values.guardianSurname}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.guardianSurname && Boolean(errors.guardianSurname)}
                helperText={touched.guardianSurname && errors.guardianSurname}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Home Phone no
              </Typography>
              <PhoneInput
                country={"gb"} // Default country
                value={values.homePhone}
                onChange={(phone) => setFieldValue("homePhone", phone)}
                onBlur={handleBlur}
                inputProps={{
                  name: "homePhone",
                  required: true,
                  autoFocus: true,
                }}
                containerStyle={{ width: "100%" }}
                inputStyle={{ width: "100%" }}
              />
              {touched.homePhone && errors.homePhone && (
                <Typography color='error' variant='caption'>
                  {errors.homePhone}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Mobile Phone no
              </Typography>
              <PhoneInput
                country={"gb"} // Default country set to Great Britain
                value={values.mobilePhone}
                onChange={(phone) => setFieldValue("mobilePhone", phone)}
                onBlur={handleBlur}
                inputProps={{
                  name: "mobilePhone",
                  required: true,
                }}
                containerStyle={{ width: "100%" }}
                inputStyle={{ width: "100%" }}
              />
              {touched.mobilePhone && errors.mobilePhone && (
                <Typography color='error' variant='caption'>
                  {errors.mobilePhone}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Current Address
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your Current Address'
                name='guardianAddress'
                value={values.guardianAddress}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.guardianAddress && Boolean(errors.guardianAddress)}
                helperText={touched.guardianAddress && errors.guardianAddress}
              />
            </Grid>
          </Grid>

          <FormControlLabel
            control={
              <Checkbox
                name='confirm'
                checked={values.confirm}
                onChange={handleChange}
                onBlur={handleBlur}
                color='primary'
              />
            }
            label={
              <Typography sx={{ fontSize: "12px", color: "black" }}>
                Please confirm that all information provided is accurate and up-to-date.
              </Typography>
            }
            error={touched.confirm && Boolean(errors.confirm)}
          />
          {touched.confirm && errors.confirm && <Typography color='error'>{errors.confirm}</Typography>}

          <Box mt={2}>
            <Typography sx={{ fontSize: "14px", color: "black" }}>
              Please sign below your consent for your child/young person to undergo any vaccinations, inoculations,
              dental treatment, anesthetics, emergency operations, first aid or any other treatment which may be
              considered necessary or essential in the opinion of Active care Company staff, Dentist, Doctor, or a
              medical professional attending in the case of an emergency.
            </Typography>
          </Box>

          <Box className='signature-box'>
            <Signature ref={signatureRef} />
            <Typography sx={{ fontSize: "14px", color: "black" }} align='right'>
              Parent/Guardian Signature
              <Grid className='clear-signature-button'>
                <Button variant='outlined' onClick={() => signatureRef.current?.clearSignature()}>
                  Clear
                </Button>
              </Grid>
            </Typography>
          </Box>

          <Box mt={2}>
            <Typography sx={{ fontSize: "14px", color: "black" }}>
              Please sign below your consent for your child/young person to be given ‘over the counter remedies 'inline
              with Active Care Organisation medication policy (enclosed)
            </Typography>
          </Box>

          <Box className='signature-box'>
            <Signature ref={signatureSecondRef} />
            <Typography sx={{ fontSize: "14px", color: "black" }} align='right'>
              Parent/Guardian Signature
              <Grid className='clear-signature-button'>
                <Button variant='outlined' onClick={() => signatureSecondRef.current?.clearSignature()}>
                  Clear
                </Button>
              </Grid>
            </Typography>
          </Box>
          <Grid item xs={12}>
            <div className='submit-button-container'>
              <Button type='submit' variant='contained' color='primary' className='submit-button'>
                Submit
              </Button>
            </div>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default AccessHealthCareRecordsForm;
